import api from 'axios';
import { Product, ProductWithCalculations, StatusType, CNPJGroup } from '../shared/types/inventory';
import { statusConfigs } from '../shared/utils/calculations';

interface ApiResponse {
  success: boolean;
  columns: string[];
  data: string[][];
}

function parseRows<T>(columns: string[], data: string[][]): T[] {
  return data.map((row) => {
    const obj:any = {};
    columns.forEach((col, i) => {
      obj[col] = row[i] ?? '';
    });
    return obj as T;
  });
}

interface EstoqueRow {
  IDLojista: string;
  CNPJLojista: string;
  RazaoSocial: string;
  EAN: string;
  NomeProduto: string;
  Foto?: string;
  SaldoInicial: string;
  Entrada: string;
  Saida: string;
  SaldoFinal: string;
  EstoqueAtual: string;
  MesAnoReferencia: string;
}

interface PedidoRow {
  IDLojista: string;
  CNPJLojista: string;
  RazaoSocial: string;
  PeriodDays: string;
  EAN: string;
  NomeProduto: string;
  Foto: string;
  Fornecedor: string;
  LeadTime: string;
  EstoqueAtual: string;
  VendasPeriodo: string;
  PrecoUnitario: string;
  VendaValor: string;
  CompraValor: string;
  PrecoMedioVenda: string;
  CustoMedioCompra: string;
  MargemBrutaEstimada: string;
  ValorEstoqueEstimado: string;
  SaidaPerdas: string;
  DivergenciaEstoque: string;
  QuantidadePedida: string;
  QuantidadeEntregue: string;
  OtifPontualidadePct: string;
  OtifCompletudePct: string;
  OtifPct: string;
  CMD: string;
  SS: string;
  ROP: string;
  CoberturaDias: string;
  EstoqueAlvo: string;
  PedidoSugerido: string;
  Classificacao: string;
  MesAnoReferencia: string;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapExtendedMetrics(row: PedidoRow) {
  return {
    vendaValor: optionalNumber(row.VendaValor),
    compraValor: optionalNumber(row.CompraValor),
    precoMedioVenda: optionalNumber(row.PrecoMedioVenda),
    custoMedioCompra: optionalNumber(row.CustoMedioCompra),
    margemBrutaEstimada: optionalNumber(row.MargemBrutaEstimada),
    valorEstoqueEstimado: optionalNumber(row.ValorEstoqueEstimado),
    saidaPerdas: optionalNumber(row.SaidaPerdas),
    divergenciaEstoque: optionalNumber(row.DivergenciaEstoque),
    quantidadePedida: optionalNumber(row.QuantidadePedida),
    quantidadeEntregue: optionalNumber(row.QuantidadeEntregue),
    otifPontualidadePct: optionalNumber(row.OtifPontualidadePct),
    otifCompletudePct: optionalNumber(row.OtifCompletudePct),
    otifPct: optionalNumber(row.OtifPct),
  };
}

function getDaysInMonth(dateStr: string): number {
  const date = new Date(dateStr);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}


function mapClassificacaoToStatus(classificacao: string): StatusType {
  const normalized = classificacao.trim().toLowerCase();
  if (normalized.includes('sem venda')) return 'sem_venda';
  if (normalized.includes('abaixo do m') || normalized.includes('ruptura iminente')) return 'iminente';
  if (normalized.includes('perto do m')) return 'minimo';
  if (normalized.includes('cobertura cr')) return 'critica';
  if (normalized.includes('excesso')) return 'excesso';
  if (normalized.includes('saud')) return 'saudavel';
  if (normalized.includes('ruptura instalada')) return 'ruptura';
  return 'saudavel';
}

function aggregateEstoqueToProducts(rows: EstoqueRow[]): Product[] {
  const grouped:any = new Map<string, EstoqueRow[]>();

  for (const row of rows) {
    const ean = row.EAN.trim();
    if (!grouped.has(ean)) {
      grouped.set(ean, []);
    }
    grouped.get(ean)!.push(row);
  }

  const products: Product[] = [];
  let idCounter = 1;

  for (const [ean, eanRows] of grouped) {
    const sorted = eanRows.sort(
      (a:any, b:any) => new Date(b.MesAnoReferencia).getTime() - new Date(a.MesAnoReferencia).getTime()
    );
    const latest = sorted[0];
    const totalSaida = sorted.reduce((sum:any, r:any) => sum + Number(r.Saida), 0);
    const totalDias = sorted.reduce((sum:any, r:any) => sum + getDaysInMonth(r.MesAnoReferencia), 0);

    products.push({
      id: idCounter++,
      ean,
      nome: latest.NomeProduto,
      foto: latest.Foto?.trim() || undefined,
      estoqueAtual: Number(latest.EstoqueAtual),
      vendasPeriodo: totalSaida,
      diasPeriodo: totalDias || 30,
    });
  }

  return products;
}

function parsePedidoToProducts(rows: PedidoRow[]): ProductWithCalculations[] {
  const grouped:any = new Map<string, PedidoRow[]>();

  for (const row of rows) {
    const ean = row.EAN.trim();
    if (!grouped.has(ean)) {
      grouped.set(ean, []);
    }
    grouped.get(ean)!.push(row);
  }

  const products: ProductWithCalculations[] = [];
  let idCounter = 1;

  for (const [ean, eanRows] of grouped) {
    const sorted = eanRows.sort(
      (a:any, b:any) => new Date(b.MesAnoReferencia).getTime() - new Date(a.MesAnoReferencia).getTime()
    );
    const latest = sorted[0];
    const cmd = Number(latest.CMD);
    const ss = Number(latest.SS);
    const rop = Number(latest.ROP);
    const status = mapClassificacaoToStatus(latest.Classificacao);

    products.push({
      id: idCounter++,
      ean,
      nome: latest.NomeProduto.trim(),
      foto: latest.Foto?.trim() || undefined,
      fornecedor: latest.Fornecedor?.trim() || undefined,
      leadTime: Number.isFinite(Number(latest.LeadTime))
        ? Number(latest.LeadTime)
        : undefined,
      estoqueAtual: Number(latest.EstoqueAtual),
      vendasPeriodo: Number(latest.VendasPeriodo) || cmd * (Number(latest.PeriodDays) || 30),
      diasPeriodo: Number(latest.PeriodDays) || 30,
      precoUnitario:
        latest.PrecoUnitario !== '' && Number.isFinite(Number(latest.PrecoUnitario))
          ? Number(latest.PrecoUnitario)
          : undefined,
      ...mapExtendedMetrics(latest),
      cmd,
      ddlt: rop - ss,
      ss,
      rop,
      cobertura: Number(latest.CoberturaDias),
      estoqueAlvo: Number(latest.EstoqueAlvo),
      pedidoSugerido: Number(latest.PedidoSugerido),
      status,
      statusLabel: statusConfigs[status].label,
    });
  }

  return products;
}

function parsePedidoToCNPJGroups(rows: PedidoRow[]): CNPJGroup[] {
  const cnpjMap:any = new Map<string, { razaoSocial: string; rows: PedidoRow[] }>();

  for (const row of rows) {
    const cnpj = row.CNPJLojista?.trim() || 'SEM_CNPJ';
    if (!cnpjMap.has(cnpj)) {
      cnpjMap.set(cnpj, { razaoSocial: row.RazaoSocial?.trim() || '', rows: [] });
    }
    cnpjMap.get(cnpj)!.rows.push(row);
  }

  const groups: CNPJGroup[] = [];

  for (const [cnpj, { razaoSocial, rows: cnpjRows }] of cnpjMap) {
    const eanMap:any = new Map<string, PedidoRow[]>();
    for (const row of cnpjRows) {
      const ean = row.EAN.trim();
      if (!eanMap.has(ean)) eanMap.set(ean, []);
      eanMap.get(ean)!.push(row);
    }

    const products: ProductWithCalculations[] = [];
    let idCounter = 1;

    for (const [ean, eanRows] of eanMap) {
      const sorted = eanRows.sort(
        (a:any, b:any) => new Date(b.MesAnoReferencia).getTime() - new Date(a.MesAnoReferencia).getTime()
      );
      const latest = sorted[0];
      const cmd = Number(latest.CMD);
      const ss = Number(latest.SS);
      const rop = Number(latest.ROP);
      const status = mapClassificacaoToStatus(latest.Classificacao);

      products.push({
        id: idCounter++,
        ean,
        nome: latest.NomeProduto.trim(),
        foto: latest.Foto?.trim() || undefined,
        fornecedor: latest.Fornecedor?.trim() || undefined,
        leadTime: Number.isFinite(Number(latest.LeadTime))
          ? Number(latest.LeadTime)
          : undefined,
        estoqueAtual: Number(latest.EstoqueAtual),
        vendasPeriodo: Number(latest.VendasPeriodo) || cmd * (Number(latest.PeriodDays) || 30),
        diasPeriodo: Number(latest.PeriodDays) || 30,
        precoUnitario:
          latest.PrecoUnitario !== '' && Number.isFinite(Number(latest.PrecoUnitario))
            ? Number(latest.PrecoUnitario)
            : undefined,
        ...mapExtendedMetrics(latest),
        cmd,
        ddlt: rop - ss,
        ss,
        rop,
        cobertura: Number(latest.CoberturaDias),
        estoqueAlvo: Number(latest.EstoqueAlvo),
        pedidoSugerido: Number(latest.PedidoSugerido),
        status,
        statusLabel: statusConfigs[status].label,
      });
    }

    groups.push({ cnpj, razaoSocial, products });
  }

  return groups;
}

export const fetchGiraEstoqueLoja = async (): Promise<Product[]> => {
  const response = await api.get<ApiResponse>('/sellers/gira_estoque_loja');
  const { columns, data } = response.data;
  const rows = parseRows<EstoqueRow>(columns, data);
  return aggregateEstoqueToProducts(rows);
};

export const fetchPedidoSugerido = async (): Promise<ProductWithCalculations[]> => {
  const response = await api.get<ApiResponse>('/sellers/gira_pedido_sugerido');
  const { columns, data } = response.data;
  const rows = parseRows<PedidoRow>(columns, data);
  return parsePedidoToProducts(rows);
};

export const fetchPedidoSugeridoGrouped = async (): Promise<CNPJGroup[]> => {
  const response = await api.get<ApiResponse>('/sellers/gira_pedido_sugerido');
  const { columns, data } = response.data;
  const rows = parseRows<PedidoRow>(columns, data);
  return parsePedidoToCNPJGroups(rows);
};
